import { useEffect, useRef } from 'react';

import {
  useQueryClient,
  useQuery as useTanstackQuery,
} from '@tanstack/react-query';

import { useAPIClient } from '@src/api';
import { useMutation } from '@src/query';

import { getDeepAnalysis, startDeepAnalysis } from './deepAnalysisApi';
import { priceEstimateKey } from './usePriceEstimate';


/**
 * Poll cadence for a deep-analysis row (M14 P6c). PURE so the interval decision
 * is unit-testable in the DOM-less node test env (same rationale as the chip's
 * `estimateTone`). Polls every 2500ms while the run is `"pending"`, and stops
 * (`false`) once it settles (`"succeeded"` / `"failed"`) or there is no row.
 *
 * @param {object|null} row  the latest mapped row.
 * @returns {number|false} the `refetchInterval` value.
 */
function pollInterval(row) {
  return row?.status === 'pending' ? 2500 : false;
}


/**
 * Stable string key for the three inputs a run is bound to (M14 P6c). PURE so the
 * reset-on-change decision below is unit-testable in the DOM-less node env.
 *
 * @param {{partType?: string, ymm?: string, condition?: string}} inputs
 * @returns {string}
 */
function inputsKey({ partType, ymm, condition }) {
  return `${partType || ''}|${ymm || ''}|${condition || ''}`;
}


/**
 * Decide whether a started run must be discarded because the part inputs changed
 * (M14 P6c). PURE/unit-testable. A run is scoped to one (partType, ymm, condition)
 * triple, but the start mutation's `data` (and thus `resultId`) survives closure
 * changes — so after a start the poll would keep rendering the OLD part's row
 * under the NEW inputs. Returns `true` only when we already have a PRIOR key
 * (i.e. not the initial mount) and the new inputs differ from it, so the caller
 * resets the mutation back to idle for the new inputs.
 *
 * @param {string|null} prevKey  the last-seen `inputsKey`, or `null` on mount.
 * @param {string} nextKey       the current `inputsKey`.
 * @returns {boolean}
 */
function shouldResetOnInputChange(prevKey, nextKey) {
  return prevKey !== null && prevKey !== nextKey;
}


/**
 * Deep price-analysis hook (Slice P6c). Composes:
 *   - a START mutation (`start()` / `isStarting` / `locked`) that POSTs the run;
 *     a 403-`upgrade_required` resolves to `{ locked: true }` (not an error), so
 *     the caller renders the upsell instead of a toast.
 *   - a POLL query keyed on `[…, sellerId, resultId, askingPrice]`, enabled only
 *     once a `resultId` exists, that `refetchInterval`s every 2500ms while the
 *     latest row is `"pending"` and stops otherwise (`pollInterval`). The v5
 *     `refetchInterval: (query) => …` signature reads `query.state.data`.
 *
 * When the row settles to `"succeeded"` the backend has already invalidated its
 * server-side estimate cache, so we invalidate the TanStack estimate query too
 * (`priceEstimateKey`) — the chip refetches and auto-upgrades to the tier-20
 * result. Guarded by a ref so it fires once per result id, not on every poll.
 *
 * @returns {{ row: object|null, locked: boolean, isStarting: boolean,
 *   start: () => void }} the presentational state for `DeepPriceAnalysis`.
 */
function useDeepAnalysis({
  sellerId, partType, ymm, condition, askingPrice,
}) {
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();

  // Route the start through the shared mutation wrapper (`@src/query` →
  // `useBaseMutation`) for convention alignment: its `defaultOnError` only toasts
  // 403s, so 400/500/network errors follow the app-wide convention of NO toast
  // (they're surfaced by the caller/query state, not swallowed by a fallback toast).
  // The 403 `upgrade_required` case never reaches onError anyway — `startDeepAnalysis`
  // RESOLVES it to `{ row: null, locked: true }` (no throw) — so the upsell still
  // renders with no toast, exactly as before.
  const startMutation = useMutation({
    mutateFn: ({ apiClient: client }) => startDeepAnalysis({
      apiClient: client, sellerId, partType, ymm, condition,
    }),
  });

  const { row: startedRow = null, locked = false } = startMutation.data || {};
  const resultId = startedRow?.id || null;

  // Reset the start mutation when the (partType, ymm, condition) triple changes
  // after a run: `startMutation.data` (and thus `resultId`/`locked`) survives
  // closure changes, so without this the poll would keep rendering the OLD part's
  // row under the NEW inputs. Guarded by `inputsKeyRef` so it never fires on mount
  // (only once we've recorded a prior key). `reset` is a stable ref from v5.
  const { reset: resetStart } = startMutation;
  const inputsKeyRef = useRef(null);
  useEffect(() => {
    const nextKey = inputsKey({ partType, ymm, condition });
    if (shouldResetOnInputChange(inputsKeyRef.current, nextKey)) {
      resetStart();
    }
    inputsKeyRef.current = nextKey;
  }, [partType, ymm, condition, resetStart]);

  const pollQuery = useTanstackQuery({
    queryKey: ['root', 'pricing', 'deep-analysis', sellerId, resultId, askingPrice || ''],
    queryFn: ({ signal }) => getDeepAnalysis({
      apiClient, sellerId, resultId, askingPrice, signal,
    }),
    enabled: Boolean(resultId),
    refetchInterval: (query) => pollInterval(query.state.data),
  });

  // The freshest row we know of: the poll result once it lands, else the row the
  // start call returned (already "succeeded" on a 200 cache hit, "pending" on 202).
  const row = pollQuery.data || startedRow;

  // Invalidate the sibling estimate query exactly once per result id when the run
  // succeeds, so the chip refetches into the upgraded tier.
  const invalidatedFor = useRef(null);
  useEffect(() => {
    if (row?.status === 'succeeded' && invalidatedFor.current !== resultId) {
      invalidatedFor.current = resultId;
      queryClient.invalidateQueries({
        queryKey: priceEstimateKey({ partType, ymm, condition }),
      });
    }
  }, [row?.status, resultId, partType, ymm, condition, queryClient]);

  return {
    row,
    locked,
    isStarting: startMutation.isPending,
    start: () => startMutation.mutate(),
  };
}


export {
  inputsKey, pollInterval, shouldResetOnInputChange, useDeepAnalysis,
};
export default useDeepAnalysis;
